import type {
  AdditionalFieldDelegate,
  AdditionalFieldDelegateProps,
} from "@firecms/core";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import type { Locale } from "../localization/constants";
import type { ActionLogCollectionEntry } from "../types/participantProgress";

export type DelaySeverity = "ok" | "mild" | "warn" | "alert" | "critical" | "skew";

const SEVERITY_CLASS: Record<DelaySeverity, string> = {
  ok: "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200",
  mild: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  warn: "bg-amber-50 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200",
  alert:
    "bg-orange-100 text-orange-900 font-semibold dark:bg-orange-950/60 dark:text-orange-200",
  critical:
    "bg-rose-100 text-rose-900 font-bold ring-1 ring-inset ring-rose-300 dark:bg-rose-950/60 dark:text-rose-200 dark:ring-rose-800",
  skew: "bg-violet-50 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200",
};

function toDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (
    value != null &&
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate: () => Date }).toDate === "function"
  ) {
    const date = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

/** writtenAt − actionAt in milliseconds. */
export function actionLogDelayMs(
  actionAt: unknown,
  writtenAt: unknown
): number | null {
  const action = toDate(actionAt);
  const written = toDate(writtenAt);
  if (!action || !written) {
    return null;
  }
  return written.getTime() - action.getTime();
}

export function delaySeverity(ms: number): DelaySeverity {
  if (ms < 0) return "skew";
  if (ms < 5_000) return "ok";
  if (ms < 60_000) return "mild";
  if (ms < 15 * 60_000) return "warn";
  if (ms < 60 * 60_000) return "alert";
  return "critical";
}

export function formatDelay(ms: number): string {
  const abs = Math.abs(ms);
  const sign = ms < 0 ? "−" : "";

  if (abs < 1_000) {
    return `${sign}${Math.round(abs)}ms`;
  }
  if (abs < 60_000) {
    const seconds = abs / 1_000;
    return `${sign}${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`;
  }
  if (abs < 60 * 60_000) {
    const minutes = Math.floor(abs / 60_000);
    const seconds = Math.round((abs % 60_000) / 1_000);
    return seconds > 0 ? `${sign}${minutes}m ${seconds}s` : `${sign}${minutes}m`;
  }
  if (abs < 24 * 60 * 60_000) {
    const hours = Math.floor(abs / (60 * 60_000));
    const minutes = Math.round((abs % (60 * 60_000)) / 60_000);
    return minutes > 0 ? `${sign}${hours}h ${minutes}m` : `${sign}${hours}h`;
  }
  const days = Math.floor(abs / (24 * 60 * 60_000));
  const hours = Math.round((abs % (24 * 60 * 60_000)) / (60 * 60_000));
  return hours > 0 ? `${sign}${days}d ${hours}h` : `${sign}${days}d`;
}

function ActionLogDelayCell({
  entity,
}: AdditionalFieldDelegateProps<ActionLogCollectionEntry>) {
  const ms = actionLogDelayMs(entity.values.actionAt, entity.values.writtenAt);
  if (ms == null) {
    return <span className="text-xs text-surface-400">—</span>;
  }

  const severity = delaySeverity(ms);
  const label = formatDelay(ms);
  const written = toDate(entity.values.writtenAt);
  const title = written
    ? `Written at ${written.toLocaleString()}`
    : undefined;

  return (
    <span
      title={title}
      className={[
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs tabular-nums whitespace-nowrap",
        SEVERITY_CLASS[severity],
      ].join(" ")}
    >
      {label}
    </span>
  );
}

export function buildActionLogDelayField(
  locale: Locale = getAdminUiLocale()
): AdditionalFieldDelegate<ActionLogCollectionEntry> {
  const t = getAdminT(locale);
  return {
    key: "delay",
    name: t("participantProgress.delayColumn"),
    width: 110,
    dependencies: ["actionAt", "writtenAt"],
    Builder: ActionLogDelayCell,
    value: ({ entity }) => {
      const ms = actionLogDelayMs(
        entity.values.actionAt,
        entity.values.writtenAt
      );
      return ms == null ? "" : formatDelay(ms);
    },
  };
}
